import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { signIdToken, verifyIdToken } from "./shareToken.server";

const SessionId = z.string().min(8).max(64);
const Email = z.string().trim().toLowerCase().email().max(255);

const Step1 = z.object({
  full_name: z.string().trim().min(1).max(120),
  email: Email,
  phone: z.string().trim().min(7).max(32),
  state: z.string().length(2),
  heard_about: z.enum([
    "friend-family",
    "social-media",
    "search",
    "podcast",
    "ct-event",
    "other",
  ]),
});

const Step2 = z.object({
  license_status: z.enum(["life", "life-health", "in-process", "not-yet"]),
  license_lines: z.array(z.string().max(40)).max(60).default([]),
  years_experience: z.enum(["0", "1-2", "3-5", "6-10", "10+"]),
  current_situation: z.enum([
    "full-time-switch",
    "part-time",
    "career-changer",
    "recent-grad",
    "returning",
  ]),
  income_target: z.enum([
    "3-5k",
    "5-10k",
    "10-20k",
    "20k+",
    "not-focused",
  ]),
});

const Step3 = z.object({
  why_currency_thoughts: z.string().trim().min(30).max(500),
  success_vision: z.string().trim().min(30).max(500),
  additional_notes: z.string().trim().max(500).optional().or(z.literal("")),
  consent_contractor: z.literal(true),
  consent_discovery_call: z.literal(true),
});

const PartialStep1 = Step1.partial();
const PartialStep2 = Step2.partial();
const PartialStep3 = Step3.partial().extend({
  consent_contractor: z.boolean().optional(),
  consent_discovery_call: z.boolean().optional(),
});

const DraftPayload = z.object({
  ...PartialStep1.shape,
  ...PartialStep2.shape,
  ...PartialStep3.shape,
  fit_score: z.number().int().min(0).max(100).optional(),
});

export const upsertDraft = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        session_id: SessionId,
        step: z.number().int().min(0).max(3),
        payload: DraftPayload,
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { session_id, step, payload } = data;

  // Derive boolean has_license from license_status string
  const derived: Record<string, unknown> = { ...payload };
  if (typeof payload.license_status === "string") {
    derived.has_license =
      payload.license_status === "life" ||
      payload.license_status === "life-health" ||
      payload.license_status === "in-process";
  }

    // Find by session_id first (preferred)
    const { data: existingBySession } = await supabaseAdmin
      .from("advisor_applications")
      .select("id, email, session_id, status, step_completed")
      .eq("session_id", session_id)
      .maybeSingle();

    let existing: { id: string; email: string | null; session_id: string | null; status: string; step_completed?: number } | null =
      existingBySession ?? null;

    // If no session match but email provided, check collision
    if (!existing && payload.email) {
      const { data: byEmail } = await supabaseAdmin
        .from("advisor_applications")
        .select("id, email, session_id, status")
        .ilike("email", payload.email)
        .maybeSingle();
      if (byEmail) {
        if (byEmail.session_id && byEmail.session_id !== session_id) {
          return {
            ok: false as const,
            error:
              "An application with this email already exists. Check your inbox for our follow-up.",
          };
        }
        existing = byEmail;
      }
    }

    if (existing && existing.status !== "new") {
      return { ok: false as const, error: "Application already submitted." };
    }

    const nextStep = Math.max(existing?.step_completed ?? 0, step);
    const baseRow = {
      ...derived,
      session_id,
      step_completed: nextStep,
      status: "new" as const,
    };

    if (existing) {
      const { error } = await supabaseAdmin
        .from("advisor_applications")
        .update(baseRow as never)
        .eq("id", existing.id);
      if (error) return { ok: false as const, error: error.message };
      return { ok: true as const, id: existing.id };
    } else {
      if (!payload.email || !payload.full_name) {
        return {
          ok: false as const,
          error: "Name and email are required to start an application.",
        };
      }
      const insertRow = {
        ...baseRow,
        full_name: payload.full_name,
        email: payload.email,
      };
      const { data: inserted, error } = await supabaseAdmin
        .from("advisor_applications")
        .insert(insertRow as never)
        .select("id")
        .single();
      if (error) return { ok: false as const, error: error.message };
      return { ok: true as const, id: inserted.id };
    }
  });

export const loadDraftBySession = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ session_id: SessionId }).parse(input),
  )
  .handler(async ({ data }) => {
    const { data: row } = await supabaseAdmin
      .from("advisor_applications")
      .select("*")
      .eq("session_id", data.session_id)
      .maybeSingle();
    if (!row || row.status !== "new") return { draft: null };
    return {
      draft: {
        full_name: row.full_name ?? "",
        email: row.email ?? "",
        phone: row.phone ?? "",
        state: row.state ?? "",
        heard_about: row.heard_about ?? "",
        license_status: (row as { license_status?: string }).license_status ?? "",
        license_lines: row.license_lines ?? [],
        years_experience: row.years_experience ?? "",
        current_situation: row.current_situation ?? "",
        income_target: row.income_target ?? "",
        why_currency_thoughts: row.why_currency_thoughts ?? "",
        success_vision: row.success_vision ?? "",
        additional_notes: row.additional_notes ?? "",
        consent_contractor: !!row.consent_contractor,
        consent_discovery_call: !!row.consent_discovery_call,
        step_completed: row.step_completed ?? 0,
        fit_score: row.fit_score ?? null,
      },
    };
  });

const signToken = (id: string): string => signIdToken(id);
const verifyToken = (token: string): string | null => verifyIdToken(token);

async function notifyNewApplication(id: string) {
  // TODO: integrate Resend admin notification email
  // TODO: integrate Resend applicant confirmation email
  // TODO: optional Slack webhook
  console.log(`[notify-new-application] new strategist application ${id}`);
}

export const submitApplication = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ session_id: SessionId }).parse(input),
  )
  .handler(async ({ data }) => {
    const { data: row, error: findErr } = await supabaseAdmin
      .from("advisor_applications")
      .select("*")
      .eq("session_id", data.session_id)
      .maybeSingle();
    if (findErr || !row) {
      return { ok: false as const, error: "Draft not found." };
    }

    // Validate full submission
    try {
      Step1.parse(row);
      Step2.parse(row);
      Step3.parse(row);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Validation failed";
      return { ok: false as const, error: msg };
    }

    const { error: upErr } = await supabaseAdmin
      .from("advisor_applications")
      .update({ step_completed: 3, status: "new" })
      .eq("id", row.id);
    if (upErr) return { ok: false as const, error: upErr.message };

    await supabaseAdmin.from("activity_logs").insert({
      action: "advisor_application.submitted",
      target_type: "advisor_application",
      target_id: row.id,
      metadata: { email: row.email, fit_score: row.fit_score },
    });

    await notifyNewApplication(row.id);

    return { ok: true as const, token: signToken(row.id) };
  });

export const loadConfirmation = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ token: z.string().min(8).max(200) }).parse(input),
  )
  .handler(async ({ data }) => {
    const id = verifyToken(data.token);
    if (!id) return { ok: false as const, error: "Invalid token." };
    const { data: row } = await supabaseAdmin
      .from("advisor_applications")
      .select("full_name, email")
      .eq("id", id)
      .maybeSingle();
    if (!row) return { ok: false as const, error: "Not found." };
    const first = (row.full_name || "").trim().split(/\s+/)[0] || "there";
    return { ok: true as const, first_name: first };
  });

export const scheduleDiscoveryCall = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        session_id: SessionId,
        scheduled_at: z.string().datetime(),
        url: z.string().url().max(500),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { data: row } = await supabaseAdmin
      .from("advisor_applications")
      .select("id")
      .eq("session_id", data.session_id)
      .maybeSingle();
    if (!row) return { ok: false as const, error: "Not found." };
    const { error } = await supabaseAdmin
      .from("advisor_applications")
      .update({
        discovery_call_scheduled_at: data.scheduled_at,
        discovery_call_url: data.url,
      })
      .eq("id", row.id);
    if (error) return { ok: false as const, error: error.message };
    await supabaseAdmin.from("activity_logs").insert({
      action: "advisor_application.discovery_scheduled",
      target_type: "advisor_application",
      target_id: row.id,
      metadata: { url: data.url },
    });
    return { ok: true as const };
  });